import React from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Flame, ArrowUpRight, CalendarClock, Zap, Sparkles } from 'lucide-react';
import { Modal } from '../common/Modal';
import { useLanguage } from '../../features/language/useLanguage';
import type { SubscriptionTier } from '../../services/subscriptionService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  tier: SubscriptionTier;
  limitKg: number;
  usedKg: number;
  requestedKg: number;
  cycleEnd: Date;
}

// Maps the current tier to the next available tier (null = no upgrade path).
const NEXT_TIER: Record<SubscriptionTier, SubscriptionTier | null> = {
  free: 'basic',
  basic: 'plus',
  plus: null,
};

export const RoastQuotaExceededModal: React.FC<Props> = ({
  isOpen,
  onClose,
  tier,
  limitKg,
  usedKg,
  requestedKg,
  cycleEnd,
}) => {
  const { t } = useLanguage();
  const nextTier = NEXT_TIER[tier];
  const resetDate = format(cycleEnd, 'PPP');
  const body =
    tier === 'plus'
      ? t('subscription.quotaModal.bodyPlus', { date: resetDate })
      : tier === 'basic'
        ? t('subscription.quotaModal.bodyBasic')
        : t('subscription.quotaModal.bodyFree');

  const upgradeCtaLabel =
    nextTier === 'plus'
      ? t('subscription.quotaModal.cta.upgradeToPlus')
      : t('subscription.quotaModal.cta.upgradeToBasic');

  const NextIcon = nextTier === 'plus' ? Sparkles : Zap;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('subscription.quotaModal.title')}
      maxWidth="md"
    >
      <div className="space-y-5" data-test-id="quota-exceeded-modal">
        {/* Hero — warm gradient block with flame emblem */}
        <div className="relative -mx-6 -mt-2 overflow-hidden bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 px-6 py-7">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-12 -right-10 h-40 w-40 rounded-full bg-orange-300/30 blur-3xl"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-16 -left-12 h-44 w-44 rounded-full bg-amber-300/30 blur-3xl"
          />
          <div className="relative flex items-start gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-orange-500/30 ring-1 ring-white/40">
              <Flame className="h-7 w-7" strokeWidth={2.25} />
            </div>
            <div className="min-w-0 pt-0.5">
              <p className="text-sm font-medium uppercase tracking-[0.18em] text-amber-700/80">
                {t('subscription.quotaModal.eyebrow')}
              </p>
              <p className="mt-1.5 text-[15px] leading-snug text-gray-800">{body}</p>
            </div>
          </div>
        </div>

        {/* Usage stat block */}
        <div className="rounded-xl border border-gray-200 bg-white px-4 py-3.5">
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
              {t('subscription.quotaModal.cycleUsage')}
            </span>
            <span className="text-sm font-semibold tabular-nums text-gray-900" data-test-id="quota-usage-numbers">
              {t('subscription.quotaModal.usage', { used: usedKg, limit: limitKg })}
            </span>
          </div>
          <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="h-full w-full rounded-full bg-gradient-to-r from-amber-500 to-red-500" />
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
            <span className="inline-flex items-center gap-1.5" data-test-id="quota-attempted">
              <span className="font-medium text-gray-700 tabular-nums">+{requestedKg} kg</span>
              {t('subscription.quotaModal.attempted')}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarClock className="h-3.5 w-3.5" />
              <span className="tabular-nums">{t('subscription.quotaModal.resetsOn', { date: resetDate })}</span>
            </span>
          </div>
        </div>

        {/* Upgrade preview — only when there's a higher tier */}
        {nextTier && (
          <Link
            to="/settings/subscription"
            onClick={onClose}
            className="group flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 px-4 py-3 transition-all hover:border-amber-300 hover:from-amber-100 hover:to-orange-100"
            data-test-id="quota-upgrade-preview"
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm shadow-amber-500/30">
                <NextIcon className="h-4 w-4" />
              </span>
              <div className="min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700">
                  {t(`subscription.tier.${nextTier}`)}
                </p>
                <p className="mt-0.5 text-sm font-semibold text-gray-900 truncate">
                  {t(`subscription.quotaModal.nextTierLine.${nextTier}`)}
                </p>
              </div>
            </div>
            <ArrowUpRight className="h-4 w-4 shrink-0 text-amber-700 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </Link>
        )}

        {/* CTAs */}
        <div className="flex items-center justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            data-test-id="quota-modal-dismiss"
          >
            {t('subscription.quotaModal.cta.dismiss')}
          </button>
          {nextTier && (
            <Link
              to="/settings/subscription"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-1.5 rounded-md bg-gradient-to-br from-primary-500 to-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm shadow-orange-500/30 transition-all hover:from-amber-600 hover:to-orange-700"
              data-test-id="quota-modal-upgrade"
            >
              {upgradeCtaLabel}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          )}
        </div>
      </div>
    </Modal>
  );
};
